// src/pages/patients/PatientOtherReports.jsx

import React, { useContext, useEffect, useState } from "react";

import {
  Box,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from "@mui/material";

import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContextProvider.js";
import moment from "moment";
import { Helmet } from "react-helmet";
import { useFetchRegistry } from "../../api/collection";
import nopreview from "../../assets/images/others/no-preview.png";

import UploadReportModal from "../../components/Reusables/UploadModel/UploadReportModal.jsx";

const PatientOtherReports = ({ patient, onCountsChange }) => {
  const { open } = useContext(AuthContext);

  // fetch reports
  const [args] = useState({
    userId: patient?.id || patient?._id,
  });

  const {
    report = [],
    isLoading,
  } = useFetchRegistry(args);

  // local state
  const [reportData, setReportData] = useState(
    Array.isArray(report) ? report : []
  );

  const [showUploadModal, setShowUploadModal] =
    useState(false);

  useEffect(() => {
    setReportData(Array.isArray(report) ? report : []);
  }, [report]);

  // notify parent count
  useEffect(() => {
    if (!isLoading) {
      onCountsChange?.({
        otherReportsCount: Array.isArray(reportData)
          ? reportData.length
          : 0,
      });
    }
  }, [reportData, isLoading, onCountsChange]);

  const REPORT_TYPE_MAP = {
    ECG: "ECG",
    BLOOD_TEST: "Blood Test",
    XRAY: "X Ray",
    OTHER: "Other",

    IP_ADMISSION_NOTE: "IP Admission Note",
    PATIENT_CASE_RECORD: "Patient Case Record",
    PATIENT_ASSESSMENT: "Patient Assessment",
    PROGRESS_NOTE: "Progress Note",
    TREATMENT_CHART: "Treatment Chart",
    MEDICAL_PRESCRIPTION: "Medical Prescription",
    REFERRAL_LETTER: "Referral Letter",
    HANDOVER_TRANSFER_FORM:
      "Handover / Transfer Form",
    DISCHARGE_CLOSURE_SUMMARY:
      "Discharge / Closure Summary",
  };

  const openUpload = () => {
    setShowUploadModal(true);
  };

  return (
    <>
      <Helmet>
        <title>Spandan - Other Reports</title>
      </Helmet>

      {Array.isArray(reportData) &&
      reportData.length > 0 ? (
        <>
          <TableContainer className="app-table-card"
            component={Paper}
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
            }}
          >
            <Table
              sx={{
                borderCollapse: "separate",
                borderSpacing: "0 12px",
              }}
            >
              <TableHead>
                <TableRow sx={{ borderRadius: 2 }}>
                  <TableCell
                    sx={{
                      color: "var(--app-text-muted)",
                      fontWeight: 700,
                      fontFamily: "Figtree",
                      fontSize: 14,
                      py: 2,
                      borderBottom: "none",
                    }}
                  >
                    NAME
                  </TableCell>

                  <TableCell
                    sx={{
                      color: "var(--app-text-muted)",
                      fontWeight: 700,
                      fontFamily: "Figtree",
                      fontSize: 14,
                      textAlign: "center",
                      borderBottom: "none",
                    }}
                  >
                    REPORT TYPE
                  </TableCell>

                  <TableCell
                    sx={{
                      color: "var(--app-text-muted)",
                      fontWeight: 700,
                      fontFamily: "Figtree",
                      fontSize: 14,
                      textAlign: "right",
                      borderBottom: "none",
                    }}
                  >
                    UPLOADED ON
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {reportData.map((r, idx) => (
                  <TableRow
                    key={r?.id || idx}
                    sx={{
                      backgroundColor: "var(--app-surface)",
                      borderRadius: "8px",
                      boxShadow:
                        "0 1px 2px rgba(0,0,0,0.06)",
                      overflow: "hidden",

                      "& .MuiTableCell-root": {
                        borderBottom: "none",
                        py: 2,
                        verticalAlign: "middle",
                        backgroundColor:
                          "transparent",
                        borderRadius: "8px",
                      },

                      "&:last-child td, &:last-child th":
                        {
                          border: 0,
                        },
                    }}
                  >
                    <TableCell>
                      {r?.url ? (
                        <Link
                          to={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration:
                              "underline",
                            color: "var(--app-text)",
                            fontWeight: 500,
                          }}
                        >
                          {r?.id || "-"}
                        </Link>
                      ) : (
                        <Typography
                          sx={{
                            color: "var(--app-text)",
                            fontWeight: 500,
                          }}
                        >
                          {r?.id || "-"}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell
                      sx={{
                        textAlign: "center",
                        color: "var(--app-text)",
                      }}
                    >
                      {REPORT_TYPE_MAP[r?.type] ||
                        r?.type ||
                        "-"}
                    </TableCell>

                    <TableCell
                      sx={{
                        textAlign: "right",
                        color: "var(--app-text)",
                      }}
                    >
                      {r?.updatedAt
                        ? moment(r.updatedAt).format(
                            "DD/MM/YYYY, hh:mm a"
                          )
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Upload Button */}
          <Box
            sx={{
              position: "fixed",
              bottom: 24,
              left: open ? "17%" : "5%",
              width: open
                ? "calc(80% - 40px)"
                : "calc(95% - 40px)",
              maxWidth: "1200px",
              zIndex: 1300,
              px: 4,

              transition:
                "left 300ms cubic-bezier(.22,.9,.29,1), width 300ms cubic-bezier(.22,.9,.29,1)",

              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",

              pointerEvents: "none",
            }}
          >
            <Button
              variant="contained"
              onClick={openUpload}
              sx={{
                pointerEvents: "auto",

                backgroundColor: "#5751E9",

                textTransform: "none",

                borderRadius: 3,

                px: 2,
                py: 1.2,

                boxShadow:
                  "0 6px 12px rgba(87,81,233,0.12)",

                "&:hover": {
                  filter: "brightness(0.95)",
                },

                display: "inline-flex",

                alignItems: "center",

                gap: 1,

                fontFamily: "Figtree",

                fontWeight: 600,
              }}
            >
              Upload Record
            </Button>
          </Box>
        </>
      ) : (
        <Box
          className="content"
          sx={{
            height: "50vh",

            display: "flex",

            justifyContent: "center",

            alignItems: "center",

            flexDirection: "column",

            m: "40px 0",
          }}
        >
          <img
            src={nopreview}
            alt="no-preview"
            style={{ width: 150 }}
          />

          <Typography variant="h6" sx={{ mt: 2 }}>
            There are no other reports taken in this
            organisation
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={openUpload}
              sx={{
                backgroundColor: "#5751E9",
                textTransform: "none",
              }}
            >
              Upload Record
            </Button>
          </Box>
        </Box>
      )}

      {/* Reusable Upload Modal */}
      <UploadReportModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        patientId={patient?.id || patient?._id}
        onUploadSuccess={(res) => {
          const newReport =
            (res.reportData &&
              res.reportData[0]) ||
            null;

          if (newReport) {
            const added = {
              id: newReport._id,
              type: newReport.name,
              updatedAt: newReport.updatedAt,
              url: newReport.url,
            };

            setReportData((prev) => [
              ...prev,
              added,
            ]);
          }
        }}
      />
    </>
  );
};

export default PatientOtherReports;